/**
 * Utility for standardizing storage keys with a prefix.
 */

const PREFIX = 'lmi_';

export const getPrefixedKey = (key: string): string => 
  key.startsWith(PREFIX) ? key : `${PREFIX}${key}`;

export const getRawStorage = (type: 'localStorage' | 'sessionStorage'): Storage | null => {
  try {
    return type === 'localStorage' ? window.localStorage : window.sessionStorage;
  } catch (e) {
    return null; // Handle iOS Safari incognito or other issues gracefully
  }
};

export const storage = {
  local: {
    getItem: (key: string): string | null => {
      const s = getRawStorage('localStorage');
      return s ? s.getItem(getPrefixedKey(key)) : null;
    },
    setItem: (key: string, value: string): void => {
      const s = getRawStorage('localStorage');
      if (s) s.setItem(getPrefixedKey(key), value);
    },
    removeItem: (key: string): void => {
      const s = getRawStorage('localStorage');
      if (s) s.removeItem(getPrefixedKey(key));
    }
  },
  session: {
    getItem: (key: string): string | null => {
      const s = getRawStorage('sessionStorage');
      return s ? s.getItem(getPrefixedKey(key)) : null;
    },
    setItem: (key: string, value: string): void => {
      const s = getRawStorage('sessionStorage');
      if (s) s.setItem(getPrefixedKey(key), value);
    },
    removeItem: (key: string): void => {
      const s = getRawStorage('sessionStorage');
      if (s) s.removeItem(getPrefixedKey(key));
    }
  },
  cookie: {
    getItem: (key: string): string | null => {
      const name = getPrefixedKey(key) + "=";
      const decodedCookie = decodeURIComponent(document.cookie);
      const ca = decodedCookie.split(';');
      for(let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) == ' ') {
          c = c.substring(1);
        }
        if (c.indexOf(name) === 0) {
          return c.substring(name.length, c.length);
        }
      }
      return null;
    },
    setItem: (key: string, value: string, days?: number): void => {
      let expires = "";
      if (days) {
        const date = new Date();
        date.setTime(date.getTime() + (days*24*60*60*1000));
        expires = "; expires=" + date.toUTCString();
      }
      document.cookie = getPrefixedKey(key) + "=" + (value || "")  + expires + "; path=/";
    },
    removeItem: (key: string): void => {
      document.cookie = getPrefixedKey(key) + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
    }
  }
};

/** Remove legacy full-profile cache; session user lives in memory only. */
export function clearLegacyUserProfileCache(): void {
  storage.local.removeItem('user');
}

/**
 * Migrate existing keys to the new prefixed format.
 * This should be called once when the app loads (e.g., in App.tsx or index.ts).
 */
export const migrateStorage = () => {
  const explicitKeys = [
    'theme',
    'i18nextLng',
    'gps_allowed',
    'user_location',
    'user_country',
    'user_country_code',
    'search_country_code',
    'search_country_name',
    'search_country_source',
    'search_country_scope_set',
    'user_state',
    'user_location_area',
    'user_pincode',
    'user_lat',
    'user_lng',
    'user_geo_accuracy',
    'user_location_source',
    'user_location_catalog_enabled',
    'geo_session_key',
    'has_asked_location',
    'onboarding_completed',
    'user_phone',
    'fcm_token_sent',
    'access_token',
    'refresh_token',
    'auth_token',
    'active_role',
    'prev_user_id',
    'user',
    'firebase_google_id_token',
    'firebase_google_email',
    'firebase_facebook_id_token',
    'firebase_facebook_email',
    'firebase_phone_id_token',
    'push_auto_synced',
    'compare_last_viewed',
    'id',
    'state'
  ];

  const prefixMatches = ['active_org_', 'public_config_cache:'];

  const shouldMigrate = (key: string): boolean => {
    if (key.startsWith(PREFIX)) return false;
    if (explicitKeys.includes(key)) return true;
    return prefixMatches.some(prefix => key.startsWith(prefix));
  };

  // 1. Migrate localStorage
  const locStorage = getRawStorage('localStorage');
  if (locStorage) {
    const keys = [];
    for (let i = 0; i < locStorage.length; i++) {
        const key = locStorage.key(i);
        if (key && shouldMigrate(key)) keys.push(key);
    }
    keys.forEach(key => {
        const val = locStorage.getItem(key);
        if (val !== null) {
            locStorage.setItem(getPrefixedKey(key), val);
            locStorage.removeItem(key);
        }
    });
  }

  // 2. Migrate sessionStorage
  const sesStorage = getRawStorage('sessionStorage');
  if (sesStorage) {
    const keys = [];
    for (let i = 0; i < sesStorage.length; i++) {
        const key = sesStorage.key(i);
        if (key && shouldMigrate(key)) keys.push(key);
    }
    keys.forEach(key => {
        const val = sesStorage.getItem(key);
        if (val !== null) {
            sesStorage.setItem(getPrefixedKey(key), val);
            sesStorage.removeItem(key);
        }
    });
  }

  // 3. Migrate cookies
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  const cookiePairs: {key: string, val: string}[] = [];
  
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i].trim();
    const eqIdx = c.indexOf('=');
    if (eqIdx > -1) {
        const key = c.substring(0, eqIdx);
        const val = c.substring(eqIdx + 1);
        if (shouldMigrate(key)) {
            cookiePairs.push({ key, val });
        }
    }
  }

  cookiePairs.forEach(({ key, val }) => {
      storage.cookie.setItem(key, val, 365);
      document.cookie = key + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
  });

  // Full user profiles must not persist in localStorage (legacy cache).
  clearLegacyUserProfileCache();

  // Legacy global assistant chat blob (pre user-scoped keys).
  try {
    sessionStorage.removeItem('lmi_assistant_chat_state');
  } catch {
    // ignore
  }
};
